// src/i18n/locales/en/analytics.ts
export const enAnalyticsTranslations = {
  analytics: {
    // Main page
    title: "Analytics",
    subtitle: "Track your restaurant performance and insights",
    comingSoon: "Coming Soon",
    comingSoonDescription:
      "We're building powerful analytics tools to help you understand your restaurant's performance, customer behavior, and growth opportunities.",

    // Action buttons
    notifyMe: "Notify Me When Ready",
    learnMore: "Learn More",
    tabs: {
      items: "Items",
      sales: "Sales",
      clients: "Clients",
    },

    // Features section
    whatToExpect: "What to Expect",
    soon: "Soon",
    inDevelopment: "In Development",
    expectedLaunch: "Expected Launch",

    // Feature descriptions
    features: {
      salesReports: "Sales Reports",
      salesReportsDesc:
        "Comprehensive reports on daily, weekly, and monthly sales performance with detailed breakdowns.",

      customerInsights: "Customer Insights",
      customerInsightsDesc:
        "Understand your customer behavior, preferences, and dining patterns to improve service.",

      performanceMetrics: "Performance Metrics",
      performanceMetricsDesc:
        "Track key performance indicators including revenue growth, order frequency, and peak hours.",

      orderTracking: "Order Analytics",
      orderTrackingDesc:
        "Monitor order trends, average order value, and identify your best-selling items.",

      menuViews: "Menu Analytics",
      menuViewsDesc:
        "See which menu items get the most views and engagement from your customers.",

      popularItems: "Popular Items",
      popularItemsDesc:
        "Identify trending dishes and optimize your menu based on customer preferences.",
    },

    // Preview section
    previewTitle: "Analytics Dashboard Preview",
    previewSubtitle: "Get a glimpse of the insights you'll soon have access to",
    chartPreview: "Interactive charts and graphs coming soon",

    // Status messages
    dataLoading: "Loading analytics data...",
    noDataAvailable: "No data available yet",

    // Time periods
    today: "Today",
    thisWeek: "This Week",
    thisMonth: "This Month",
    lastMonth: "Last Month",

    // Metrics
    totalRevenue: "Total Revenue",
    totalOrders: "Total Orders",
    totalSales: "Total Sales",
    averageOrderValue: "Average Order Value",
    customerRetention: "Customer Retention",
    topSellingItems: "Top Selling Items",
    peakHours: "Peak Hours",
    sales: "Sales",
    branchIdNotFound: "Branch ID not found",
    failedToFetchBranchStartDate: "Failed to fetch branch start date",
    errorFetchingBranchStartDate: "An error occurred while fetching branch start date",
    contactViaWhatsApp: "Contact via WhatsApp",

    // New analytics dashboard keys
    totalClicks: "Total Clicks",
    avgTimeSpent: "Avg Time Spent",
    totalItems: "Total Items",
    mostPopular: "Most Popular",
    noData: "No data",

    abrevHour: "H",
    monthlyTrends: "Monthly Trends",
    itemPerformance: "Item Performance",
    itemDetails: "Item Details",
    clicks: "Clicks",
    timeSpent: "Time Spent",
    avgTime: "Avg Time",
    views: "Views",
    searchItems: "Search items...",
    noItemsFound: "No items found",
    tryDifferentSearch: "Try a different search term",
    priceNotSet: "Price not set",
    engagement: "Engagement",
    allCategories: "All Categories",
    allYears: "All Years",
    allMonths: "All Months",
    timeFilters: "Time Period Filters",
    selectYear: "Select Year",
    selectMonth: "Select Month",
    showingAllData: "Showing data for all time periods",
    showingDataFor: "Showing data for",
    resetFilters: "Reset Filters",
    branchSalesSummary: "Branch Sales Summary",
    updatedAutomatically: "Updated automatically",
    period: "Period",
    latest: "Latest",
    yearly: "Yearly",
    monthly: "Monthly",
    daily: "Daily",

    // Sales Analytics Tab
    branchSalesOverview: "Branch Sales Overview",
    branchSalesDescription: "Monitor your branch's overall sales performance",
    selectDateRange: "Select Date Range",
    dateRangeDescription: "Choose dates to analyze your sales data",
    quickOptions: "Quick Options",
    customRange: "Custom Range",
    dailySalesOrders: "Daily sales and orders overview",
    salesTrend: "Sales Trend",
    itemsBreakdown: "Items Breakdown",
    itemPerformanceComparison: "Item Performance Comparison",
    itemPerformanceDescription:
      "Compare performance metrics across selected items",
    performanceDateRange: "Performance Date Range",
    aiSales: "AI Sales",
    regularSales: "Regular Sales",
    avgOrderValue: "Avg Order Value",
    orders: "orders",
    failedLoadSalesData: "Failed to load sales data. Please try again.",
    failedLoadPerformanceData:
      "Failed to load performance data. Please try again.",

    // Clients Analytics Tab
    searchClients: "Search by name...",
    noClients: "No clients found",
    clientName: "Name",
    clientPhone: "Phone",
    lastOrder: "Last Order",
    clientOrders: "Orders",
    clientTotalSpent: "Total Spent",
    clientAvgOrder: "Avg Order",
    contact: "Contact",

    // Date Picker & Presets
    from: "From",
    to: "To",
    startDate: "Start date",
    endDate: "End date",
    last7Days: "Last 7 Days",
    last30Days: "Last 30 Days",
    yesterday: "Yesterday",

    // Items Analytics
    selectItemsToCompare: "Select Items to Compare",
    selected: "selected",
    maxItemsSelected:
      "Maximum of {{maxItems}} items selected. Remove an item to add more.",
    searchItemsPlaceholder: "Search items by name or category...",
    categories: "Categories",
    sortedByClicks: "Sorted by clicks",
    topPerformers: "Top Performers",
    noTopItems: "No top items found for the selected period",
    allItems: "All Items",
    noItems: "No items found for the selected filters",
    totalTimeSpent: "Total Time Spent",
    avgTimePerClick: "Avg. Time / Click",

    // Item Breakdown Table
    itemBreakdown: {
      title: "Items Breakdown",
      noData: "No items data available",
      items: "items",
      item: "Item",
      orders: "Orders",
      revenue: "Revenue",
      aiOrders: "AI Orders",
      aiRevenue: "AI Revenue",
      ofTotal: "of total",
      ai: "AI",
      unknown: "Unknown",
    },

    // Item Performance Table
    itemPerformanceTable: {
      title: "Items Performance Summary",
      selectItemsPrompt: "Select items above to view their performance",
      rankedByRevenue: "Ranked by total revenue",
      item: "Item",
      totalOrders: "Total Orders",
      totalRevenue: "Total Revenue",
      avgValue: "Avg Value",
      orders: "Orders",
      revenue: "Revenue",
      avg: "Avg",
      unknownCategory: "Unknown category",
    },

    // Performance Comparison Chart
    performanceComparison: {
      title: "Performance Comparison",
      selectItemsPrompt: "Select items to view their performance comparison",
      monthlyRevenue: "Monthly Revenue Comparison",
      revenueTrends: "Revenue trends across selected items",
      monthlyOrders: "Monthly Orders Comparison",
      orderTrends: "Order quantity trends across selected items",
      item: "Item",
    },

    // Chart
    noDataForPeriod: "No data available for the selected period",

    // Months
    months: {
      january: "January",
      february: "February",
      march: "March",
      april: "April",
      may: "May",
      june: "June",
      july: "July",
      august: "August",
      september: "September",
      october: "October",
      november: "November",
      december: "December",
    },
  },
};
